/**
 * CM Wizard — Document Upload path.
 *
 * Flow:
 *   1. Pick file (PDF or image)
 *   2. Convert to base64, POST /cm-wizard/document/parse, get syncJobId
 *   3. Poll /cm-wizard/document/parse/:id every 2s until status='succeeded'
 *   4. Show parsed line items in an editable table — client confirms or
 *      edits each row
 *   5. On Confirm: POST /cm-wizard/document/confirm with both the original
 *      parse + the (possibly edited) confirmed items. Backend records the
 *      diff for each edited field via the learning loop, then commits the
 *      cm_inputs.
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import CmWizardPathNav from "../components/CmWizardPathNav.js";

interface ParsedLineItem {
  rawIdentifier: string;
  description: string;
  quantity?: number;
  unitCost?: number;
  amount: number;
  currency: string;
  category?: string;
  confidence: number;
}

interface ParseResultData {
  lineItems: ParsedLineItem[];
  totalDetectedAmount: number;
  currency: string;
  modelNotes?: string;
  sourceDocumentRef?: string;
  usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number };
}

interface ParseStatus {
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  progressCurrent: number | null;
  progressTotal: number | null;
  errorMessage: string | null;
  parseResult: ParseResultData | null;
}

type DocumentKind = "supplier_invoice" | "pl_statement";

export default function CmWizardDocumentPage(): React.ReactElement {
  const api = useApiClient();
  const [file, setFile] = useState<File | null>(null);
  const [documentKind, setDocumentKind] = useState<DocumentKind>("supplier_invoice");
  const [syncJobId, setSyncJobId] = useState<string | null>(null);
  const [edited, setEdited] = useState<ParsedLineItem[] | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file picked");
      const base64 = await fileToBase64(file);
      const result = await api.post<{ syncJobId: string }>("/cm-wizard/document/parse", {
        documentBase64: base64,
        mediaType: file.type,
        documentKind,
      });
      return result;
    },
    onSuccess: (data) => {
      setSyncJobId(data.syncJobId);
      setEdited(null);
    },
  });

  const status = useQuery({
    queryKey: ["cm-wizard", "document", "parse", syncJobId],
    queryFn: () => api.get<ParseStatus>(`/cm-wizard/document/parse/${syncJobId}`),
    enabled: Boolean(syncJobId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      if (data.status === "succeeded" || data.status === "failed" || data.status === "cancelled") return false;
      return 2000;
    },
  });

  // Initialize edited rows from parse result the first time it lands.
  if (status.data?.parseResult && edited === null) {
    setEdited(status.data.parseResult.lineItems);
  }

  const confirm = useMutation({
    mutationFn: () => {
      if (!syncJobId || !edited) throw new Error("Nothing to confirm");
      return api.post("/cm-wizard/document/confirm", {
        syncJobId,
        confirmedLineItems: edited,
        // Stage 1: confirmedLineItems are informational — the frontend doesn't
        // yet auto-build cm_input drafts from invoice rows. Phase 1.7 wires
        // the line-item-to-SKU mapping. For now the wizard's manual path is
        // where cm_inputs land; this flow just records learnings.
        drafts: [],
        applyTo: "forward",
      });
    },
    onSuccess: () => {
      window.alert("Confirmed. Your corrections were recorded — the next parse will be smarter.");
      setFile(null);
      setSyncJobId(null);
      setEdited(null);
    },
  });

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24 }}>Upload supplier invoice or P&L</h1>
      <p style={{ color: "var(--body)", marginTop: 6 }}>
        We'll read the document and pre-fill the line items. You confirm or fix
        what we got wrong. Every fix makes the next parse smarter.
      </p>

      <div style={{ marginTop: 24 }}>
        <CmWizardPathNav />
      </div>

      {!syncJobId && (
        <UploadStep
          file={file}
          documentKind={documentKind}
          onFileChange={setFile}
          onKindChange={setDocumentKind}
          onParse={() => upload.mutate()}
          parsing={upload.isPending}
          error={upload.error ? String(upload.error) : null}
        />
      )}

      {syncJobId && status.data?.status !== "succeeded" && status.data?.status !== "failed" && (
        <div style={pendingStyles.box}>
          <p style={{ margin: 0 }}>Reading your document…</p>
          <p style={{ color: "var(--body)", marginTop: 6, fontSize: 13 }}>
            Usually 10-30 seconds. Multi-page PDFs can take a minute.
          </p>
        </div>
      )}

      {status.data?.status === "failed" && (
        <div style={errorStyles.box}>
          <p style={{ margin: 0, color: "var(--bad)" }}>Something broke.</p>
          <p style={{ marginTop: 6, fontSize: 13 }}>{status.data.errorMessage ?? "Unknown error"}</p>
          <button
            style={buttonStyles.secondary}
            onClick={() => {
              setSyncJobId(null);
              setEdited(null);
            }}
          >
            Start over
          </button>
        </div>
      )}

      {status.data?.parseResult && edited && (
        <ConfirmStep
          parseResult={status.data.parseResult}
          edited={edited}
          onEdit={setEdited}
          onConfirm={() => confirm.mutate()}
          confirming={confirm.isPending}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------

function UploadStep({
  file,
  documentKind,
  onFileChange,
  onKindChange,
  onParse,
  parsing,
  error,
}: {
  file: File | null;
  documentKind: DocumentKind;
  onFileChange: (f: File | null) => void;
  onKindChange: (k: DocumentKind) => void;
  onParse: () => void;
  parsing: boolean;
  error: string | null;
}): React.ReactElement {
  return (
    <div style={uploadStyles.box}>
      <label style={uploadStyles.kindRow}>
        <span style={uploadStyles.label}>Document type:</span>
        <select
          value={documentKind}
          onChange={(e) => onKindChange(e.target.value as DocumentKind)}
          style={uploadStyles.select}
        >
          <option value="supplier_invoice">Supplier invoice</option>
          <option value="pl_statement">P&L statement</option>
        </select>
      </label>

      <div
        style={uploadStyles.dropZone}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) onFileChange(f);
        }}
      >
        <input
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          id="cm-doc-upload"
          style={{ display: "none" }}
          onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        />
        <label htmlFor="cm-doc-upload" style={uploadStyles.pickButton}>
          {file ? "Choose a different file" : "Choose file"}
        </label>
        <p style={uploadStyles.dropHint}>or drop a PDF / PNG / JPEG here</p>
        {file && (
          <p style={uploadStyles.fileName}>
            {file.name} ({formatFileSize(file.size)})
          </p>
        )}
      </div>

      {error && <p style={{ color: "var(--bad)", marginTop: 12 }}>{error}</p>}

      <button
        style={buttonStyles.primary}
        onClick={onParse}
        disabled={!file || parsing}
      >
        {parsing ? "Uploading…" : "Read the document"}
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------

function ConfirmStep({
  parseResult,
  edited,
  onEdit,
  onConfirm,
  confirming,
}: {
  parseResult: ParseResultData;
  edited: ParsedLineItem[];
  onEdit: (rows: ParsedLineItem[]) => void;
  onConfirm: () => void;
  confirming: boolean;
}): React.ReactElement {
  const updateRow = (idx: number, field: keyof ParsedLineItem, value: string | number): void => {
    onEdit(
      edited.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    );
  };

  return (
    <div style={confirmStyles.box}>
      <div style={confirmStyles.summary}>
        <span style={confirmStyles.summaryLabel}>Detected total:</span>
        <span style={confirmStyles.summaryAmount}>
          {formatCurrency(parseResult.totalDetectedAmount, parseResult.currency)}
        </span>
        <span style={{ color: "var(--body)", fontSize: 12 }}>
          {parseResult.lineItems.length} line items
        </span>
      </div>

      {parseResult.modelNotes && (
        <div style={confirmStyles.notes}>
          <strong>Notes from the parser:</strong> {parseResult.modelNotes}
        </div>
      )}

      <div style={tableStyles.scroll}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={tableStyles.th}>Identifier</th>
              <th style={tableStyles.th}>Description</th>
              <th style={tableStyles.th}>Qty</th>
              <th style={tableStyles.th}>Unit cost</th>
              <th style={tableStyles.th}>Amount</th>
              <th style={tableStyles.th}>Category</th>
              <th style={tableStyles.th}>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {edited.map((row, idx) => (
              <tr key={`${row.rawIdentifier}-${idx}`}>
                <td style={tableStyles.td}>
                  <CellInput value={row.rawIdentifier} onChange={(v) => updateRow(idx, "rawIdentifier", v)} />
                </td>
                <td style={tableStyles.td}>
                  <CellInput value={row.description} onChange={(v) => updateRow(idx, "description", v)} />
                </td>
                <td style={tableStyles.td}>
                  <CellNumber
                    value={row.quantity ?? ""}
                    onChange={(v) => updateRow(idx, "quantity", v === "" ? 0 : Number(v))}
                  />
                </td>
                <td style={tableStyles.td}>
                  <CellNumber
                    value={row.unitCost ?? ""}
                    onChange={(v) => updateRow(idx, "unitCost", v === "" ? 0 : Number(v))}
                  />
                </td>
                <td style={tableStyles.td}>
                  <CellNumber
                    value={row.amount}
                    onChange={(v) => updateRow(idx, "amount", Number(v))}
                  />
                </td>
                <td style={tableStyles.td}>
                  <CellInput value={row.category ?? ""} onChange={(v) => updateRow(idx, "category", v)} />
                </td>
                <td style={{ ...tableStyles.td, color: confidenceColor(row.confidence) }}>
                  {(row.confidence * 100).toFixed(0)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "var(--body)", fontSize: 12 }}>
          Tokens used: in {parseResult.usage.inputTokens.toLocaleString()}, out{" "}
          {parseResult.usage.outputTokens.toLocaleString()}
          {parseResult.usage.cacheReadTokens > 0 && ` (cache ${parseResult.usage.cacheReadTokens.toLocaleString()})`}
        </span>
        <button style={buttonStyles.primary} onClick={onConfirm} disabled={confirming}>
          {confirming ? "Saving…" : "Confirm + save"}
        </button>
      </div>
    </div>
  );
}

function CellInput({ value, onChange }: { value: string; onChange: (v: string) => void }): React.ReactElement {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={cellStyles.input}
    />
  );
}

function CellNumber({
  value,
  onChange,
}: {
  value: number | string;
  onChange: (v: string) => void;
}): React.ReactElement {
  return (
    <input
      type="number"
      value={value === "" ? "" : String(value)}
      onChange={(e) => onChange(e.target.value)}
      style={cellStyles.numberInput}
      step="0.01"
    />
  );
}

// ----------------------------------------------------------------------------

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:application/pdf;base64,xxxxxx" — strip the prefix
      const commaIdx = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function confidenceColor(c: number): string {
  if (c >= 0.85) return "var(--good)";
  if (c >= 0.6) return "var(--c2)";
  return "var(--bad)";
}

// ----------------------------------------------------------------------------

const uploadStyles: Record<string, React.CSSProperties> = {
  box: {
    marginTop: 24,
    padding: 32,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    maxWidth: 640,
  },
  kindRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  label: {
    color: "var(--body)",
    fontSize: 14,
  },
  select: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: 14,
  },
  dropZone: {
    border: "2px dashed var(--border)",
    borderRadius: 10,
    padding: 32,
    textAlign: "center",
    background: "var(--surface-2)",
  },
  pickButton: {
    display: "inline-block",
    padding: "8px 16px",
    background: "var(--surface-2)",
    color: "var(--text)",
    borderRadius: 6,
    fontSize: 14,
    cursor: "pointer",
  },
  dropHint: {
    color: "var(--body)",
    fontSize: 13,
    marginTop: 12,
    marginBottom: 4,
  },
  fileName: {
    color: "var(--good)",
    fontSize: 13,
    marginTop: 12,
  },
};

const buttonStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "10px 20px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 16,
  },
  secondary: {
    background: "transparent",
    color: "var(--body)",
    border: "1px solid var(--border)",
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 14,
    cursor: "pointer",
    marginTop: 12,
  },
};

const pendingStyles: Record<string, React.CSSProperties> = {
  box: {
    marginTop: 24,
    padding: 24,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
  },
};

const errorStyles: Record<string, React.CSSProperties> = {
  box: {
    marginTop: 24,
    padding: 20,
    background: "color-mix(in srgb, var(--bad) 14%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--bad) 35%, var(--border))",
    borderRadius: 10,
  },
};

const confirmStyles: Record<string, React.CSSProperties> = {
  box: {
    marginTop: 24,
  },
  summary: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    padding: "12px 16px",
    background: "var(--surface-2)",
    borderRadius: 8,
    marginBottom: 16,
  },
  summaryLabel: {
    color: "var(--body)",
    fontSize: 13,
  },
  summaryAmount: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  notes: {
    padding: "10px 14px",
    background: "color-mix(in srgb, var(--c2) 12%, var(--surface))",
    border: "1px solid color-mix(in srgb, var(--c2) 35%, var(--border))",
    borderRadius: 8,
    fontSize: 13,
    color: "var(--c2)",
    marginBottom: 16,
  },
};

const cellStyles: Record<string, React.CSSProperties> = {
  input: {
    background: "transparent",
    color: "var(--text)",
    border: "none",
    padding: "4px 0",
    fontSize: 13,
    width: "100%",
    fontFamily: "inherit",
  },
  numberInput: {
    background: "transparent",
    color: "var(--text)",
    border: "none",
    padding: "4px 0",
    fontSize: 13,
    width: 90,
    fontVariantNumeric: "tabular-nums",
    fontFamily: "inherit",
  },
};

const tableStyles: Record<string, React.CSSProperties> = {
  scroll: {
    overflowX: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 11,
    color: "var(--body)",
    fontWeight: 500,
    borderBottom: "1px solid var(--border)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 14px",
    fontSize: 13,
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
  },
};
